import { useEffect, useMemo, useState, type FormEvent } from "react";
import { useAuth } from "../auth/AuthContext";
import { useLocale } from "../../shared/i18n/LocaleContext";
import type { TranslationKey } from "../../shared/i18n/translations";
import {
  getMrp,
  exceptionStatus,
  proposalAction,
  runMrp,
  saveMrp,
  type MrpRecord,
  type MrpSnapshot,
} from "./mrpApi";
import { ClientRecordListControls } from "../../shared/components/ClientRecordListControls";
import { useClientRecordList } from "../../shared/components/useClientRecordList";
import { SortableHeader } from "../../shared/components/SortableHeader";
import { AppModal } from "../../shared/components/AppModal";
const k = (x: string) => x as TranslationKey;
export type MrpSection =
  "overview" | "forecasts" | "parameters" | "proposals" | "runs" | "requirements" | "pegging" | "exceptions";
export function MrpPage({ section = "overview" }: { section?: MrpSection }) {
  const { t } = useLocale(),
    { user } = useAuth(),
    [data, setData] = useState<MrpSnapshot>(),
    [error, setError] = useState(""),
    [autoFirm,setAutoFirm]=useState(false),
    [editing, setEditing] = useState<MrpRecord | null | undefined>();
  const load = async () => {
    try {
      setData(await getMrp());
      setError("");
    } catch (e) {
      setError(e instanceof Error ? e.message : "mrp.loadError");
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const write = user?.permissions.mrp === "Write";
  if (error)
    return (
      <main className="page-content">
        <p className="form-error" role="alert">
          {t(k(error))}
        </p>
      </main>
    );
  if (!data)
    return (
      <main className="page-content">
        <p>{t(k("mrp.loading"))}</p>
      </main>
    );
  if (editing !== undefined) {
    const editableRecords = section === "forecasts" ? data.forecasts : section === "parameters" ? data.parameters : [];
    const recordIndex = editing ? editableRecords.findIndex((record) => record.id === editing.id) : -1;
    return (
      <Editor
        section={section}
        item={editing}
        data={data}
        close={() => setEditing(undefined)}
        saved={async () => {
          setEditing(undefined);
          await load();
        }}
        onPrevious={recordIndex > 0 ? () => setEditing(editableRecords[recordIndex - 1]) : null}
        onNext={recordIndex >= 0 && recordIndex < editableRecords.length - 1 ? () => setEditing(editableRecords[recordIndex + 1]) : null}
      />
    );
  }
  return (
    <main className="page-content">
      <header className="welcome administration-heading">
        <div>
          <span className="eyebrow">{t(k("mrp.eyebrow"))}</span>
          <h1>{t(k(`mrp.${section}`))}</h1>
          <p>{t(k(`mrp.${section}Description`))}</p>
        </div>
        {write && section === "overview" && (<div className="header-actions"><label className="checkbox-field"><input type="checkbox" checked={autoFirm} onChange={e=>setAutoFirm(e.target.checked)}/><span>{t(k("mrp.autoFirm"))}</span></label>
          <button
            className="primary-button"
            onClick={async () => {
              await runMrp({
                horizonStartUtc: new Date().toISOString(),
                horizonDays: 180,autoFirm,
              });
              await load();
            }}
          >
            {t(k("mrp.run"))}
          </button></div>
        )}
      </header>
      {section === "overview" ? (
        <Overview data={data} />
      ) : (
        <Table
          section={section}
          data={data}
          write={write}
          reload={load}
          edit={setEditing}
        />
      )}
    </main>
  );
}
function Overview({ data }: { data: MrpSnapshot }) {
  const { t, formatNumber } = useLocale();
  return (
    <>
      <section className="maintenance-kpis quality-kpis">
        {Object.entries(data.overview).map(([x, v]) => (
          <article className="indicator-card neutral maintenance-kpi" key={x}>
            <span className="indicator-label">{t(k(`mrp.kpi.${x}`))}</span>
            <div className="indicator-value">{formatNumber(v)}</div>
          </article>
        ))}
      </section>
      <section className="capacity-overview-grid">
        {(["forecasts", "parameters", "proposals", "requirements", "pegging", "exceptions", "runs"] as const).map(
          (x) => (
            <a
              className="capacity-overview-card"
              href={`/app/mrp/${x === "forecasts" ? "previsiones" : x === "parameters" ? "parametros" : x === "proposals" ? "propuestas" : x === "requirements" ? "necesidades" : x === "exceptions" ? "excepciones" : x === "pegging" ? "pegging" : "ejecuciones"}`}
              key={x}
            >
              <h2>{t(k(`mrp.${x}`))}</h2>
              <p>{t(k(`mrp.${x}Description`))}</p>
              <span className="capacity-overview-link">
                {t("capacity.openSection")} →
              </span>
            </a>
          ),
        )}
      </section>
    </>
  );
}
function Table({
  section,
  data,
  write,
  reload,
  edit,
}: {
  section: MrpSection;
  data: MrpSnapshot;
  write: boolean;
  reload: () => Promise<void>;
  edit: (row: MrpRecord | null) => void;
}) {
  const { t, formatNumber, formatDateTime } = useLocale();
  const [firmOptions,setFirmOptions]=useState<Record<string,string>>({});
  const records = useMemo(
    () =>
      section === "forecasts"
        ? data.forecasts
        : section === "parameters"
          ? data.parameters
          : section === "proposals"
            ? data.proposals
            : section === "requirements"
              ? data.requirements
              : section === "pegging"
                ? data.pegging
                : section === "exceptions"
                  ? data.exceptions
            : data.runs,
    [section, data],
  );
  const list = useClientRecordList(records, `mrp:${section}`);
  const article = (id: unknown) => data.articles.find((x) => x.id === id);
  return (
    <section className="data-panel administration-panel">
      <div className="section-heading">
        <h2>{t(k(`mrp.${section}`))}</h2>
        {write && (section === "forecasts" || section === "parameters") && (
          <button className="primary-button" type="button" onClick={() => edit(null)}>
            + {t("administration.add")}
          </button>
        )}
      </div>
      <ClientRecordListControls list={list} formatStatus={value => t(k(`mrp.status.${value}`))} />
      <div className="administration-table-wrap">
        <table className="data-table administration-table">
          <thead>
            <tr>
              <SortableHeader label={t("materials.article")} active={list.sort === "articleId"} direction={list.direction} onClick={() => list.changeSort("articleId")} />
              <SortableHeader label={t(k("mrp.quantity"))} active={list.sort === "quantity"} direction={list.direction} onClick={() => list.changeSort("quantity")} />
              <SortableHeader label={t(k("mrp.date"))} active={list.sort === "requiredAtUtc"} direction={list.direction} onClick={() => list.changeSort("requiredAtUtc")} />
              <SortableHeader label={t("planning.status")} active={list.sort === "status"} direction={list.direction} onClick={() => list.changeSort("status")} />
              <th>
                <span className="visually-hidden">
                  {t("administration.actions")}
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            {list.rows.map((row) => (
              <tr key={row.id}>
                <td>
                  {section==="exceptions"?t(k(String(row.code))):section==="pegging"?String(row.demandRequirementId):<>{String(article(row.articleId)?.code ?? row.number ?? "—")} ·{" "}{String(article(row.articleId)?.name ?? "")}</>}
                </td>
                <td>
                  {typeof (row.quantity ?? row.netQuantity ?? row.proposalCount) === "number"
                    ? formatNumber(Number(row.quantity ?? row.netQuantity ?? row.proposalCount))
                    : "—"}
                </td>
                <td>
                  {row.requiredAtUtc || row.releaseAtUtc || row.startedAtUtc
                    ? formatDateTime(
                        String(
                          row.requiredAtUtc ??
                            row.releaseAtUtc ??
                            row.startedAtUtc,
                        ),
                      )
                    : "—"}
                </td>
                <td>
                  {t(
                    k(
                      `mrp.status.${String(row.status ?? (row.isActive === false ? "Inactive" : "Active"))}`,
                    ),
                  )}
                </td>
                <td>
                  {write &&
                    (section === "forecasts" || section === "parameters") && (
                      <button
                        className="secondary-button"
                        onClick={() => edit(row)}
                      >
                        {t("administration.edit")}
                      </button>
                    )}
                  {write &&
                    section === "proposals" &&
                    row.status === "Suggested" && (
                      <>
                        <button
                          className="primary-button"
                          onClick={async () => {
                            await proposalAction(row.id, "firm",row.kind==="Transfer"?{sourceWarehouseId:firmOptions[row.id]||null}:row.kind==="Subcontract"?{supplierId:firmOptions[row.id]||null}:{});
                            await reload();
                          }}
                        >
                          {t(k("mrp.firm"))}
                        </button>
                        {row.kind==="Transfer"&&<select aria-label={t(k("mrp.sourceWarehouse"))} value={firmOptions[row.id]??""} onChange={e=>setFirmOptions(x=>({...x,[row.id]:e.target.value}))}><option value="">{t(k("mrp.sourceWarehouse"))}</option>{data.warehouses.filter(x=>x.id!==row.warehouseId).map(x=><option key={x.id} value={x.id}>{String(x.code)} · {String(x.name)}</option>)}</select>}
                        {row.kind==="Subcontract"&&<select aria-label={t(k("mrp.supplier"))} value={firmOptions[row.id]??""} onChange={e=>setFirmOptions(x=>({...x,[row.id]:e.target.value}))}><option value="">{t(k("mrp.supplier"))}</option>{data.suppliers.map(x=><option key={x.id} value={x.id}>{String(x.code)} · {String(x.name)}</option>)}</select>}
                        <button
                          className="secondary-button"
                          onClick={async () => {
                            await proposalAction(row.id, "dismiss");
                            await reload();
                          }}
                        >
                          {t(k("mrp.dismiss"))}
                        </button>
                      </>
                    )}
                  {write && section === "exceptions" && row.status === "Open" && <button className="secondary-button" onClick={async()=>{await exceptionStatus(row.id,"Acknowledged");await reload()}}>{t(k("mrp.acknowledge"))}</button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {list.rows.length === 0 && (
        <p className="empty-state">{t(k("mrp.noResults"))}</p>
      )}
    </section>
  );
}
function Editor({
  section,
  item,
  data,
  close,
  saved,
  onPrevious,
  onNext,
}: {
  section: MrpSection;
  item: MrpRecord | null;
  data: MrpSnapshot;
  close: () => void;
  saved: () => Promise<void>;
  onPrevious?: (() => void) | null;
  onNext?: (() => void) | null;
}) {
  const { t } = useLocale(),
    [error, setError] = useState("");
  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = e.currentTarget,
      b: Record<string, unknown> = Object.fromEntries(new FormData(f));
    for (const x of [
      "quantity",
      "consumedQuantity",
      "safetyStock",
      "minimumQuantity",
      "orderMultiple",
      "maximumQuantity",
      "leadTimeDays",
      "planningTimeFenceDays",
      "priority",
    ])
      if (b[x] !== "" && b[x] !== undefined) b[x] = Number(b[x]);
    b.isActive =
      (f.elements.namedItem("isActive") as HTMLInputElement)?.checked ?? false;
    b.includeForecast =
      (f.elements.namedItem("includeForecast") as HTMLInputElement)?.checked ??
      false;
    try {
      await saveMrp(section as "parameters" | "forecasts", b, item?.id);
      await saved();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "mrp.saveError");
    }
  };
  return (
    <main className="page-content">
      <AppModal title={t(k(`mrp.${section}`))} closeLabel={t("recordNavigation.backToList")} onClose={close} onPrevious={onPrevious} onNext={onNext} saveForm wide feedback={error ? t(k(error)) : null}>
        <form id="mrp-form" className="administration-form record-form" onSubmit={submit}>
          <div className="compact-form-grid">
          <label>
            <span>{t("materials.article")}</span>
            <select
              name="articleId"
              defaultValue={String(item?.articleId ?? "")}
              required
            >
              {data.articles.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(x.code)} · {String(x.name)}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>{t("materials.warehouse")}</span>
            <select
              name="warehouseId"
              defaultValue={String(item?.warehouseId ?? "")}
              required
            >
              {data.warehouses.map((x) => (
                <option key={x.id} value={x.id}>
                  {String(x.code)} · {String(x.name)}
                </option>
              ))}
            </select>
          </label>
          {section === "forecasts" ? (
            <>
              <F n="code" l={t("aplPanel.code")} v={item?.code} required />
              <F
                n="requiredAtUtc"
                l={t(k("mrp.requiredAt"))}
                v={
                  item?.requiredAtUtc
                    ? String(item.requiredAtUtc).slice(0, 16)
                    : ""
                }
                type="datetime-local"
                required
              />
              <F
                n="quantity"
                l={t(k("mrp.quantity"))}
                v={item?.quantity ?? 0}
                type="number"
                required
              />
              <F
                n="consumedQuantity"
                l={t(k("mrp.consumed"))}
                v={item?.consumedQuantity ?? 0}
                type="number"
              />
              <F n="priority" l={t(k("mrp.priority"))} v={item?.priority ?? 100} type="number" />
              <F
                n="notes"
                l={t("administration.description")}
                v={item?.notes}
              />
            </>
          ) : (
            <>
              <label>
                <span>{t(k("mrp.lotPolicy"))}</span>
                <select
                  name="lotSizingPolicy"
                  defaultValue={String(item?.lotSizingPolicy ?? "LotForLot")}
                >
                  <option value="LotForLot">{t(k("mrp.lotForLot"))}</option>
                  <option value="MinimumAndMultiple">
                    {t(k("mrp.minimumMultiple"))}
                  </option>
                  <option value="FixedQuantity">
                    {t(k("mrp.fixedQuantity"))}
                  </option>
                  <option value="UpToMaximum">{t(k("mrp.upToMaximum"))}</option>
                </select>
              </label>
              <F
                n="safetyStock"
                l={t(k("mrp.safetyStock"))}
                v={item?.safetyStock ?? 0}
                type="number"
              />
              <F
                n="minimumQuantity"
                l={t(k("mrp.minimum"))}
                v={item?.minimumQuantity ?? 0}
                type="number"
              />
              <F
                n="orderMultiple"
                l={t(k("mrp.multiple"))}
                v={item?.orderMultiple ?? 0}
                type="number"
              />
              <F
                n="maximumQuantity"
                l={t(k("mrp.maximum"))}
                v={item?.maximumQuantity}
                type="number"
              />
              <F
                n="leadTimeDays"
                l={t(k("mrp.leadTime"))}
                v={item?.leadTimeDays ?? 0}
                type="number"
              />
              <F
                n="planningTimeFenceDays"
                l={t(k("mrp.timeFence"))}
                v={item?.planningTimeFenceDays ?? 0}
                type="number"
              />
              <Check
                n="includeForecast"
                l={t(k("mrp.includeForecast"))}
                checked={item?.includeForecast !== false}
              />
            </>
          )}
          <Check
            n="isActive"
            l={t("administration.active")}
            checked={item?.isActive !== false}
          />
          </div>
        </form>
      </AppModal>
    </main>
  );
}
function F({
  n,
  l,
  v,
  type = "text",
  required = false,
}: {
  n: string;
  l: string;
  v: unknown;
  type?: string;
  required?: boolean;
}) {
  return (
    <label>
      <span>{l}</span>
      <input
        name={n}
        defaultValue={v == null ? "" : String(v)}
        type={type}
        step={type === "number" ? "any" : undefined}
        required={required}
      />
    </label>
  );
}
function Check({ n, l, checked }: { n: string; l: string; checked: boolean }) {
  return (
    <label className="checkbox-label">
      <input name={n} type="checkbox" defaultChecked={checked} />
      <span>{l}</span>
    </label>
  );
}
